/**
 * metaPixel.js — Sprint 26-1
 * Meta Pixel (fbq) 이벤트 트래킹 헬퍼
 *
 * 위치: phase-web/src/services/metaPixel.js
 *
 * Pixel ID: 4450780945252331 (index.html에서 init됨)
 *
 * 광고 측정의 핵심 이벤트 5종:
 * - PageView           모든 페이지 진입 (index.html 자동)
 * - Lead               가입 완료 → 광고 → 가입 전환율
 * - CompleteRegistration 가입 + 첫 종목 등록 → 광고 → 활성화율
 * - InitiateCheckout   결제 페이지 진입 → 광고 → 결제 의향
 * - Purchase           결제 완료 → 광고 → 매출 (가장 중요)
 *
 * Meta 알고리즘이 Purchase 이벤트를 학습하면 "결제 가능성 높은 사람"에게 광고 자동 최적화.
 * 이게 광고비 효율의 핵심.
 */

/**
 * fbq 함수 사용 가능 여부 확인
 * Meta Pixel이 index.html에서 init 안 됐거나 차단됐을 때 안전 처리
 */
function isReady() {
  return typeof window !== "undefined" && typeof window.fbq === "function";
}

/**
 * 표준 이벤트 발사
 * @param {string} eventName Lead | CompleteRegistration | InitiateCheckout | Purchase 등
 * @param {object} params  value, currency, content_name 등
 */
function trackEvent(eventName, params = {}) {
  if (!isReady()) {
    console.warn(`[META PIXEL] fbq not ready, skipping: ${eventName}`);
    return;
  }
  try {
    window.fbq("track", eventName, params);
    console.log(`[META PIXEL] ${eventName}`, params);
  } catch (e) {
    console.error(`[META PIXEL] Track failed (${eventName}):`, e);
  }
}

// ═══════════════════════════════════════════
// 1. Lead — 회원가입 완료 시
// ═══════════════════════════════════════════
/**
 * 광고 → 가입 전환 추적
 * auth.signup 성공 시 호출
 */
export function trackSignup() {
  trackEvent("Lead", {
    content_name: "Pause Signup",
    content_category: "Investment App",
  });
}

// ═══════════════════════════════════════════
// 2. CompleteRegistration — 가입 + 첫 종목 등록
// ═══════════════════════════════════════════
/**
 * 광고 → 활성화 전환 추적 (Lead보다 의미 있는 사용자)
 * 첫 종목 등록 성공 시 호출
 */
export function trackFirstHolding() {
  trackEvent("CompleteRegistration", {
    content_name: "First Holding Added",
    status: "active",
  });
}

// ═══════════════════════════════════════════
// 3. InitiateCheckout — 결제 페이지 진입
// ═══════════════════════════════════════════
/**
 * 광고 → 결제 의향 추적
 * CheckoutModal 진입 또는 결제 버튼 클릭 시
 */
export function trackCheckoutStart(plan, price, billingCycle) {
  trackEvent("InitiateCheckout", {
    content_name: `Pause ${plan}`,
    content_category: billingCycle === "annual" ? "Annual Subscription" : "Monthly Subscription",
    value: price,
    currency: "KRW",
  });
}

// ═══════════════════════════════════════════
// 4. Purchase — 결제 완료 (가장 중요)
// ═══════════════════════════════════════════
/**
 * 광고 → 매출 전환 추적
 * Meta 알고리즘 학습의 핵심 이벤트
 *
 * @param {string} plan         "starter" | "core"
 * @param {number} price        실제 결제 금액 (KRW)
 * @param {string} billingCycle "monthly" | "annual"
 */
export function trackPurchase(plan, price, billingCycle) {
  trackEvent("Purchase", {
    content_name: `Pause ${plan}`,
    content_category: billingCycle === "annual" ? "Annual Subscription" : "Monthly Subscription",
    content_ids: [`pause_${plan}_${billingCycle}`],
    value: price,
    currency: "KRW",
  });
}

// ═══════════════════════════════════════════
// 5. 맞춤 이벤트 (Pause 전용)
// ═══════════════════════════════════════════
/**
 * 30일 무료 체험 시작 시
 * 가입 직후 또는 첫 로그인 시 1회만
 */
export function trackTrialStart() {
  trackEvent("StartTrial", {
    content_name: "Pause Core 30-Day Trial",
    value: 0,
    currency: "KRW",
  });
}

/**
 * AI 상담 첫 사용 (활성 사용자 신호)
 */
export function trackFirstAiChat() {
  trackEvent("ViewContent", {
    content_name: "First AI Chat",
    content_category: "Activation",
  });
}

// ═══════════════════════════════════════════
// Export
// ═══════════════════════════════════════════
export default {
  trackSignup,
  trackFirstHolding,
  trackCheckoutStart,
  trackPurchase,
  trackTrialStart,
  trackFirstAiChat,
};
